/**
 * Quick Add Component - Handles quick add to cart functionality
 * Manages variant selection, quantity, and cart submission from product cards
 */

import { Component } from './component-base.js';
import { ThemeBus } from './events-bus.js';
import { EVENTS } from './event-constants.js';
import { updateCartBubble } from './utilities.js';

/**
 * Custom element for quick add to cart functionality
 * @extends Component
 */
export class QuickAddComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    // Get references to key elements
    this.variantSelect = this.querySelector('select');
    this.quantitySelector = this.querySelector('quantity-selector-component');
    this.quantityInput = this.quantitySelector?.querySelector('input[name="quantity"]');
    this.addToCartButton = this.querySelector('[data-quick-add-submit]');
    this.liveRegion = this.querySelector('[data-live-region]');

    if (!this.addToCartButton) {
      console.warn('[QuickAdd] No add to cart button found', this);
      return;
    }

    // Get product ID from data attribute
    this.productId = this.dataset.productId;

    // Store button text states
    this.textAvailable = this.addToCartButton.dataset.textAvailable || 'Add to cart';
    this.textSoldOut = this.addToCartButton.dataset.textSoldOut || 'Sold out';
    this.textUnavailable = this.addToCartButton.dataset.textUnavailable || 'Unavailable';
    this.textMaxInCart = this.addToCartButton.dataset.textMaxInCart || 'Maximum in cart';

    // Set initial variant ID
    if (this.variantSelect) {
      this.currentVariantId = this.variantSelect.value || this.variantSelect.options[0]?.value;
    }

    // Fetch product data for variant information
    this.#fetchProductData();

    // Add event listeners
    this.addToCartButton.addEventListener('click', this.#handleAddToCart);

    // Update variant ID when selection changes
    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', this.#handleVariantChange);
    }

    // Add listener to quantity input to enforce max on manual entry
    if (this.quantityInput) {
      this.quantityInput.addEventListener('input', this.#enforceQuantityMax);
      this.quantityInput.addEventListener('change', this.#enforceQuantityMax);
    }

    // Listen for cart updates to check if max is in cart
    try {
      ThemeBus.addEventListener(EVENTS.cartUpdated, this.#handleCartUpdate);
    } catch (_) {}
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.addToCartButton?.removeEventListener('click', this.#handleAddToCart);
    this.variantSelect?.removeEventListener('change', this.#handleVariantChange);
    this.quantityInput?.removeEventListener('input', this.#enforceQuantityMax);
    this.quantityInput?.removeEventListener('change', this.#enforceQuantityMax);
    
    try {
      ThemeBus.removeEventListener(EVENTS.cartUpdated, this.#handleCartUpdate);
    } catch (_) {}
  }

  /**
   * Loads product data from embedded JSON in the page
   */
  #fetchProductData() {
    if (!this.productId) {
      return;
    }

    // Find the embedded product JSON by product ID
    const jsonElement = document.getElementById(`product-json-${this.productId}`);
    
    if (!jsonElement) {
      console.error(`[QuickAdd] Product JSON not found for product ID: ${this.productId}`);
      return;
    }

    try {
      this.productData = JSON.parse(jsonElement.textContent);
      
      // Update initial variant state
      if (!this.variantSelect && this.productData?.variants?.length === 1) {
        this.currentVariantId = this.productData.variants[0].id;
      }
      
      // Always update variant state if we have a current variant
      if (this.currentVariantId) {
        this.#updateVariantState(this.currentVariantId);
      }
    } catch (error) {
      console.error('[QuickAdd] Error parsing product JSON:', error);
    }
  }

  /**
   * Handles variant selection changes
   */
  #handleVariantChange = () => {
    this.currentVariantId = this.variantSelect.value;
    this.#updateVariantState(this.currentVariantId);
  };

  /**
   * Handles cart updates - recheck availability when cart changes
   */
  #handleCartUpdate = () => {
    if (this.currentVariantId) {
      this.#updateVariantState(this.currentVariantId);
    }
  };

  async #updateCartData() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      
      const cartData = await response.json();
      window.__cart = cartData;
      updateCartBubble();
    } catch (error) {
      console.error('[QuickAdd] Error fetching cart:', error);
    }
  }
  #getQuantityInCart(variantId) {
    if (!window.__cart || !window.__cart.items) {
      return 0;
    }
    
    const cartItem = window.__cart.items.find(item => item.variant_id == variantId);
    return cartItem ? cartItem.quantity : 0;
  }

  /**
   * Checks if the variant can be added to cart based on inventory and cart quantity
   * @param {number} variantId - The variant ID
   * @param {number} requestedQuantity - The quantity to add
   * @returns {boolean} Whether the add to cart should proceed
   */
  #canAddToCart(variantId, requestedQuantity) {
    try {
      const variant = this.productData?.variants?.find(v => v.id === variantId);
      if (!variant) return true; // If no product data, allow (backend will handle)
      
      // Check if inventory is managed and tracked
      if (variant.inventory_management !== 'shopify' || variant.inventory_policy !== 'deny') {
        return true; // Allow if not tracking or allowing backorders
      }
      
      const inventoryQuantity = variant.inventory_quantity || 0;
      const quantityInCart = this.#getQuantityInCart(variantId);
      const remainingQuantity = inventoryQuantity - quantityInCart;
      
      if (remainingQuantity <= 0) {
        // Show error message
        this.#setLiveRegionText('Maximum quantity already in cart');
        this.#setLoadingState(false);
        return false;
      }
      
      if (requestedQuantity > remainingQuantity) {
        // Show error message with available quantity
        this.#setLiveRegionText(`Only ${remainingQuantity} available (${quantityInCart} already in cart)`);
        this.#setLoadingState(false);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('[QuickAdd] Error checking cart quantity:', error);
      return true; // Allow on error
    }
  }

  /**
   * Enforces quantity max limit on manual input
   */
  #enforceQuantityMax = () => {
    if (!this.quantityInput) return;
    
    const max = parseInt(this.quantityInput.getAttribute('max')) || 999;
    const value = parseInt(this.quantityInput.value) || 1;
    
    if (value > max) {
      this.quantityInput.value = max;
    } else if (value < 1) {
      this.quantityInput.value = 1;
    }
  };

  /**
   * Updates button and quantity selector based on variant availability
   * @param {string|number} variantId - The variant ID to check
   */
  #updateVariantState(variantId) {
    if (!this.productData || !variantId) {
      return;
    }

    // Find the variant in product data
    const variant = this.productData.variants.find(v => v.id == variantId);
    if (!variant) {
      return;
    }

    // Check if variant is available
    const isAvailable = variant.available;
    const inventoryQuantity = variant.inventory_quantity;
    const inventoryManagement = variant.inventory_management;
    const inventoryPolicy = variant.inventory_policy;

    const quantityInCart = this.#getQuantityInCart(variantId);
    let remainingQuantity;
    
    if (inventoryManagement === 'shopify' && inventoryPolicy === 'deny') {
      remainingQuantity = Math.max(0, inventoryQuantity - quantityInCart);
    } else {
      remainingQuantity = 999;
    }

    // Update button state based on availability and cart quantity
    if (!isAvailable) {
      this.#setButtonState(false, this.textSoldOut);
      this.#updateQuantityMax(0);
    } else if (remainingQuantity <= 0) {
      // All available inventory is already in cart
      this.#setButtonState(false, this.textMaxInCart);
      this.#updateQuantityMax(0);
    } else {
      // Some quantity still available
      this.#setButtonState(true, this.textAvailable);
      this.#updateQuantityMax(remainingQuantity);
    }
  }

  /**
   * Updates the maximum quantity on the quantity selector
   * @param {number} max - The maximum quantity allowed
   */
  #updateQuantityMax(max) {
    if (!this.quantitySelector) return;

    // Directly update the quantity selector component's max property
    this.quantitySelector.max = max;
    this.quantitySelector.dataset.max = max;
    
    // Update the input's max attribute for HTML5 validation
    if (this.quantityInput) {
      this.quantityInput.setAttribute('max', max);
      
      // Reset quantity if current value exceeds new max
      const currentValue = parseInt(this.quantityInput.value) || 1;
      if (currentValue > max) {
        this.quantityInput.value = Math.max(1, max);
      }
    }
    
    // Manually trigger the update method to refresh button states
    // This is what the quantity-selector does internally when update() is called
    if (this.quantitySelector.update && typeof this.quantitySelector.update === 'function') {
      this.quantitySelector.update();
    }
  }

  /**
   * Sets the button state (enabled/disabled and text)
   * @param {boolean} enabled - Whether button should be enabled
   * @param {string} text - The text to display
   */
  #setButtonState(enabled, text) {
    const btn = this.addToCartButton;
    const label = btn.querySelector('.button-label');

    btn.disabled = !enabled;
    
    if (!enabled) {
      btn.classList.add('opacity-50', 'cursor-not-allowed');
    } else {
      btn.classList.remove('opacity-50', 'cursor-not-allowed');
    }

    if (label) {
      label.textContent = text;
    }
  }

  /**
   * Handles add to cart button click
   * @param {Event} event - The click event
   */
  #handleAddToCart = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Get variant ID
    const variantId = parseInt(this.currentVariantId || this.variantSelect?.value);
    if (!variantId) {
      this.#setLiveRegionText('Please select a variant');
      return;
    }

    // Get quantity
    const quantity = parseInt(this.quantityInput?.value || 1);

    // Check if we can add to cart
    if (!this.#canAddToCart(variantId, quantity)) {
      return; // Error message already shown
    }

    // Set loading state
    this.#setLoadingState(true);

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      const result = await response.json();

      if (result.status && result.status !== 200) {
        this.#setLiveRegionText(result.message || 'Error adding to cart');
        this.#setLoadingState(false);
        return;
      }

      // Success!
      this.#setSuccessState();
      this.#setLiveRegionText('Added to cart');
      
      // Fetch the full cart to get updated quantities
      await this.#updateCartData();

      // Short delay to show "Added!" message, then update button state
      setTimeout(() => {
        // Dispatch cart events via ThemeBus
        try {
          ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
        } catch (_) {}
        
        try {
          ThemeBus.dispatchEvent(new Event(EVENTS.cartItemAdded));
        } catch (_) {}

        // Reset quantity to 1 after successful add
        if (this.quantityInput) {
          this.quantityInput.value = 1;
          this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }, 500);

    } catch (error) {
      console.error('[QuickAdd] Error adding to cart:', error);
      this.#setLiveRegionText('Failed to add to cart');
      this.#setLoadingState(false);
    }
  };

  /**
   * Sets loading state on the button
   * @param {boolean} loading - Whether to show loading state
   */
  #setLoadingState(loading) {
    const btn = this.addToCartButton;
    const label = btn.querySelector('.button-label');
    
    if (loading) {
      btn.disabled = true;
      btn.classList.add('opacity-50', 'cursor-not-allowed');
      if (label) {
        label.dataset.originalText = label.textContent;
        label.textContent = 'Adding...';
      }
    } else {
      btn.disabled = false;
      btn.classList.remove('opacity-50', 'cursor-not-allowed');
      if (label && label.dataset.originalText) {
        label.textContent = label.dataset.originalText;
      }
    }
  }

  /**
   * Shows success state temporarily then resets
   */
  #setSuccessState() {
    const btn = this.addToCartButton;
    const label = btn.querySelector('.button-label');
    
    if (label) {
      const originalText = label.dataset.originalText || this.textAvailable;
      label.textContent = 'Added!';
      label.dataset.originalText = originalText;
      
      // Reset to original text after 2 seconds
      setTimeout(() => {
        label.textContent = originalText;
      }, 2000);
    }
  }

  /**
   * Updates the live region for screen readers and shows visual feedback for errors
   * @param {string} text - The text to announce
   */
  #setLiveRegionText(text) {
    // Update screen reader live region (always hidden)
    if (this.liveRegion) {
      this.liveRegion.textContent = text;
      setTimeout(() => {
        this.liveRegion.textContent = '';
      }, 3000);
    }
    
    // Show error messages visually in the dedicated error div
    const errorDiv = this.querySelector('[data-error-message]');
    if (!errorDiv) return;
    
    const isError = text.toLowerCase().includes('maximum') || 
                    text.toLowerCase().includes('only') || 
                    text.toLowerCase().includes('error') || 
                    text.toLowerCase().includes('failed') ||
                    text.toLowerCase().includes('select');
    
    if (isError) {
      errorDiv.textContent = text;
      errorDiv.classList.remove('hidden');
      
      // Auto-hide after 5 seconds
      setTimeout(() => {
        errorDiv.classList.add('hidden');
        errorDiv.textContent = '';
      }, 5000);
    } else {
      // Hide error message for success messages
      errorDiv.classList.add('hidden');
      errorDiv.textContent = '';
    }
  }
}

/**
 * Initialize the quick add component
 */
function initQuickAdd() {
  if (!customElements.get('quick-add-component')) {
    customElements.define('quick-add-component', QuickAddComponent);
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuickAdd);
} else {
  initQuickAdd();
}

export { initQuickAdd };


